import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormInput,
  FormNumber,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { type ReactNode, useEffect } from "react";
import { z } from "zod";
import { CreateExpenseCategorySchema } from "@/modules/expenses/domain/contracts/expense-categories";
import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import type { ExpenseCategoryDTO } from "../api/client";

const FormSchema = CreateExpenseCategorySchema.extend({
  sortOrder: z.coerce.number().int().min(0).optional(),
  isActive: z.boolean(),
});

type FormValues = z.infer<typeof FormSchema>;

type ExpenseCategoryFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: ExpenseCategoryDTO | null;
  onSubmit: (values: FormValues) => Promise<void>;
  isPending?: boolean;
};

function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2.5 rounded-lg border bg-muted/20 px-4 pt-4 pb-3">
      <div>
        <h3 className="font-medium text-sm">{title}</h3>
        {description ? (
          <p className="mt-0.5 text-muted-foreground text-xs">{description}</p>
        ) : null}
      </div>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

export function ExpenseCategoryFormDialog({
  open,
  onOpenChange,
  initial,
  onSubmit,
  isPending,
}: ExpenseCategoryFormDialogProps) {
  const methods = RHF.useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      code: "",
      name: "",
      sortOrder: 0,
      isActive: true,
    },
  });

  useEffect(() => {
    if (open) {
      methods.reset({
        code: initial?.code ?? "",
        name: initial?.name ?? "",
        sortOrder: initial?.sortOrder ?? 0,
        isActive: initial?.isActive !== false,
      });
    }
  }, [open, initial, methods]);

  const isEdit = !!initial;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton className="gap-0 p-0 sm:max-w-lg">
        <DialogHeader className="space-y-1 border-b px-6 py-5">
          <DialogTitle>{isEdit ? "ແກ້ໄຂຫມວດ" : "ສ້າງຫມວດໃໝ່"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "ອັບເດດຊື່ ລຳດັບ ແລະ ສະຖານະ — ລະຫັດບໍ່ສາມາດປ່ຽນໄດ້"
              : "ກຳນົດລະຫັດ ແລະ ຊື່ຫມວດສຳລັບຈັດກຸ່ມຄ່າໃຊ້ຈ່າຍ"}
          </DialogDescription>
        </DialogHeader>

        <FormRoot
          methods={methods}
          onSubmit={async (values) => {
            await onSubmit(values);
            onOpenChange(false);
          }}
          className="flex flex-col"
        >
          <div className="space-y-3 px-6 pt-5 pb-4">
            <FormSection
              title="ຂໍ້ມູນພື້ນຖານ"
              description="ລະຫັດໃຊ້ອ້າງອີງໃນລະບົບ — ຄວນເປັນຕົວພິມໃຫญ່ ແລະ ບໍ່ຊ້ຳກັນ"
            >
              <FormInput
                name="code"
                label="ລະຫັດ"
                placeholder="TRAVEL"
                required
                disabled={isEdit}
              />
              {!isEdit ? (
                <p className="text-muted-foreground text-xs">
                  ຕົວຢ່າງ: TRAVEL, OFFICE, MARKETING
                </p>
              ) : (
                <p className="text-muted-foreground text-xs">
                  ລະຫັດຖືກກຳນົດແລ້ວ ແລະ ບໍ່ສາມາດແກ້ໄຂໄດ້
                </p>
              )}
              <FormInput
                name="name"
                label="ຊື່ຫມວດ"
                placeholder="ຄ່າເດີນທາງ"
                required
              />
            </FormSection>

            <FormSection
              title="ການຈັດລຽງ"
              description="ຕົວເລກຍິ່ງນ້ອຍ ຈະຂຶ້ນກ່ອນໃນລາຍການເລືອກ"
            >
              <FormNumber name="sortOrder" label="ລຳດັບ" min={0} />
            </FormSection>

            <FormSection title="ສະຖານະ">
              <FormCheckboxCard
                name="isActive"
                label="ເປີດໃຊ້ງານ"
                description="ປິດໃຊ້ງານເພື່ອຊ້ອນຈາກຟອມໃໝ່ — ລາຍການເກົ່າຍັງເຫັນຫມວດນີ້"
              />
            </FormSection>
          </div>

          <DialogFooter className="border-t px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
